import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { collection, doc, getDocs, setDoc, Timestamp } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowUpRightFromSquare,
  faClockRotateLeft,
  faMoneyBillWave,
  faMoneyCheckDollar,
  faPlus,
  faWallet
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../../Pages/dash.css';
import {
  compareMonthKeys,
  formatDateTime,
  formatKes,
  formatShortDate,
  getDateTimeInputValue,
  getMonthKey,
  getMonthLabel,
  getMonthRange,
  getPaymentTimestamp,
  getBillPaymentAllocations,
  isSuccessfulSalaryPayment as isSuccessfulBillPayment,
  normalizeAmount,
  resolveBillPaymentPeriodKey,
  summarizeFixedBills
} from '../../utils/financeWorkflow';
import { logAdminActivity } from '../../utils/activityLogger';

const LOTUS_API_BASE_URL = process.env.REACT_APP_LOTUS_API_BASE_URL
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/lotusApi';
const LOTUS_RECONCILE_INTERVAL_MS = 15 * 60 * 1000;

const VENDOR_PAYMENT_METHOD_TO_LOTUS = {
  'Bank Transfer': 'pesalink',
  MPESA: 'mpesa',
  Paybill: 'paybill',
  'Till Number': 'till'
};

const normalizeVendorPaymentMethod = (method) => String(method || '').trim();

const normalizeVendorStatus = (status) => String(status || 'Active').trim();

const normalizeLotusTransactionId = (value) => String(value || '')
  .trim()
  .replace(/^TR[-_ ]*/i, '');

const normalizePaymentStatus = (value) => String(value || 'Pending').trim();

const paymentStatusTone = (value) => {
  const normalized = normalizePaymentStatus(value).toLowerCase();

  if (['paid', 'processed', 'completed', 'successful', 'success', 'succeeded'].includes(normalized)) {
    return 'success';
  }

  if (['failed', 'failure', 'rejected', 'declined', 'cancelled', 'canceled', 'error'].includes(normalized)) {
    return 'danger';
  }

  return 'warning';
};

const findNestedValue = (source, keys) => {
  const queue = [source];
  const seen = new Set();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || seen.has(current)) {
      continue;
    }

    seen.add(current);

    for (const key of keys) {
      const value = current[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return value;
      }
    }

    for (const value of Object.values(current)) {
      if (value && typeof value === 'object') {
        queue.push(value);
      }
    }
  }

  return '';
};

const extractLotusPaymentDetails = (payload) => {
  const source = payload?.data?.transaction
    || payload?.data
    || payload?.transaction
    || payload?.payment
    || payload?.result
    || payload
    || {};

  return {
    lotusTransactionId: normalizeLotusTransactionId(findNestedValue(source, [
      'transaction_id',
      'transactionId',
      'payment_id',
      'paymentId',
      'id'
    ])),
    lotusTransactionStatus: String(findNestedValue(source, [
      'status',
      'payment_status',
      'paymentStatus',
      'state',
      'result',
      'transaction_status',
      'transactionStatus'
    ])).trim(),
    lotusTransactionMessage: String(findNestedValue(payload, [
      'message',
      'detail',
      'description',
      'response_message',
      'responseMessage'
    ])).trim()
  };
};

const buildEmptyPaymentForm = () => ({
  amount: '',
  paymentDate: getDateTimeInputValue()
});

const getVendorPayoutSummary = (vendor = {}) => {
  const method = normalizeVendorPaymentMethod(vendor.vendorPaymentMethod || vendor.paymentMethod);

  if (method === 'Bank Transfer') {
    return {
      method,
      detail: [
        vendor.vendorPaymentBankName || 'Bank not set',
        vendor.vendorPaymentReference || 'Account not set'
      ].filter(Boolean).join(' • '),
      ready: Boolean(String(vendor.vendorPaymentBankId || '').trim() && String(vendor.vendorPaymentReference || '').trim())
    };
  }

  if (method === 'MPESA') {
    return {
      method,
      detail: vendor.vendorPaymentPhone || 'Phone not set',
      ready: Boolean(String(vendor.vendorPaymentPhone || '').trim())
    };
  }

  if (method === 'Paybill') {
    return {
      method,
      detail: [
        vendor.vendorPaymentPaybillNumber || 'Paybill not set',
        vendor.vendorPaymentPaybillAccountNumber || 'Account not set'
      ].filter(Boolean).join(' • '),
      ready: Boolean(
        String(vendor.vendorPaymentPaybillNumber || '').trim()
        && String(vendor.vendorPaymentPaybillAccountNumber || '').trim()
      )
    };
  }

  if (method === 'Till Number') {
    return {
      method,
      detail: vendor.vendorPaymentTillNumber || 'Till number not set',
      ready: Boolean(String(vendor.vendorPaymentTillNumber || '').trim())
    };
  }

  return {
    method: method || 'Not set',
    detail: 'Payment method not set',
    ready: false
  };
};

const getVendorDestinationDetails = (vendor = {}, method = '') => {
  if (method === 'Bank Transfer') {
    return {
      recipientName: vendor.vendorName || '',
      bankId: String(vendor.vendorPaymentBankId || '').trim(),
      bankName: String(vendor.vendorPaymentBankName || '').trim(),
      bankCode: String(vendor.vendorPaymentBankCode || '').trim(),
      bankAccountNumber: String(vendor.vendorPaymentReference || '').trim()
    };
  }

  if (method === 'MPESA') {
    return {
      recipientName: vendor.vendorName || '',
      phoneNumber: String(vendor.vendorPaymentPhone || vendor.vendorPhone || '').trim()
    };
  }

  if (method === 'Paybill') {
    return {
      recipientName: vendor.vendorName || '',
      paybillNumber: String(vendor.vendorPaymentPaybillNumber || '').trim(),
      paybillAccountNumber: String(vendor.vendorPaymentPaybillAccountNumber || '').trim()
    };
  }

  if (method === 'Till Number') {
    return {
      recipientName: vendor.vendorName || '',
      tillNumber: String(vendor.vendorPaymentTillNumber || '').trim()
    };
  }

  return null;
};

const buildBillTimeline = ({ bill, bills, vendorsById, branchesById, payments, periodKey }) => {
  const billPayments = payments.filter((payment) => (
    payment.billId === bill.id && isSuccessfulBillPayment(payment)
  ));
  const firstPaymentKey = billPayments.length > 0
    ? billPayments
      .map((payment) => resolveBillPaymentPeriodKey(payment))
      .filter(Boolean)
      .sort(compareMonthKeys)[0]
    : '';
  const startKey = firstPaymentKey || periodKey;
  const monthRange = getMonthRange(startKey, periodKey);
  const monthKeys = monthRange.length > 0 ? monthRange : [periodKey];

  return monthKeys.map((monthKey) => {
    const monthBillRows = summarizeFixedBills({
      bills: [bill],
      vendorsById,
      branchesById,
      payments,
      periodKey: monthKey
    });

    const monthBill = monthBillRows[0] || {};

    return {
      periodKey: monthKey,
      periodLabel: getMonthLabel(monthKey),
      expectedAmount: monthBill.expectedAmount || 0,
      paidAmount: monthBill.paidAmount || 0,
      remainingAmount: monthBill.remainingAmount || 0
    };
  });
};

const buildAllocationPreview = (timeline, amount) => {
  let remaining = normalizeAmount(amount);

  return timeline.map((entry) => {
    const allocatedAmount = Math.min(remaining, normalizeAmount(entry.remainingAmount));
    remaining -= allocatedAmount;

    return {
      ...entry,
      allocatedAmount
    };
  }).filter((entry) => entry.allocatedAmount > 0);
};

function PayBillsComp() {
  const auth = getAuth();
  const navigate = useNavigate();

  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [selectedBillId, setSelectedBillId] = useState('');
  const [vendorPickerTerm, setVendorPickerTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [periodKey, setPeriodKey] = useState(getMonthKey());
  const [paymentForm, setPaymentForm] = useState(buildEmptyPaymentForm());

  const loadPage = useCallback(async () => {
    const [vendorSnapshot, branchSnapshot, billSnapshot, paymentSnapshot] = await Promise.all([
      getDocs(collection(db, 'Vendors')),
      getDocs(collection(db, 'Branches')),
      getDocs(collection(db, 'Bills')),
      getDocs(collection(db, 'BillPayments'))
    ]);

    setVendors(vendorSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setBranches(branchSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setBills(billSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setPayments(paymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
  }, []);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadPage();
      } catch (error) {
        console.error('Failed to load pay bills page:', error);
        swal('Error', 'Bill payments could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [loadPage]);

  useEffect(() => {
    if (vendors.length === 0 || selectedVendorId) {
      return;
    }

    const defaultVendor = vendors.find((vendor) => normalizeVendorPaymentMethod(vendor.vendorPaymentMethod || vendor.paymentMethod)) || vendors[0];
    setSelectedVendorId(defaultVendor?.id || '');
  }, [selectedVendorId, vendors]);

  const vendorsById = useMemo(() => vendors.reduce((accumulator, vendor) => {
    accumulator[vendor.id] = vendor;
    return accumulator;
  }, {}), [vendors]);

  const branchesById = useMemo(() => branches.reduce((accumulator, branch) => {
    accumulator[branch.id] = branch.branchName;
    return accumulator;
  }, {}), [branches]);

  const vendorBills = useMemo(() => {
    if (!selectedVendorId) {
      return [];
    }

    return bills
      .filter((bill) => bill.vendor === selectedVendorId)
      .map((bill) => summarizeFixedBills({
        bills: [bill],
        vendorsById,
        branchesById,
        payments,
        periodKey
      })[0])
      .filter(Boolean)
      .sort((first, second) => {
        if (second.remainingAmount !== first.remainingAmount) {
          return second.remainingAmount - first.remainingAmount;
        }

        return second.expectedAmount - first.expectedAmount;
      });
  }, [bills, branchesById, payments, periodKey, selectedVendorId, vendorsById]);

  const selectedVendor = useMemo(
    () => vendorsById[selectedVendorId] || null,
    [selectedVendorId, vendorsById]
  );

  useEffect(() => {
    if (!selectedVendorId) {
      return;
    }

    const nextBill = vendorBills.find((bill) => bill.remainingAmount > 0) || vendorBills[0] || null;
    if (!nextBill) {
      setSelectedBillId('');
      return;
    }

    if (!selectedBillId || !vendorBills.some((bill) => bill.id === selectedBillId)) {
      setSelectedBillId(nextBill.id);
    }
  }, [selectedBillId, selectedVendorId, vendorBills]);

  const selectedBill = useMemo(
    () => vendorBills.find((bill) => bill.id === selectedBillId) || null,
    [selectedBillId, vendorBills]
  );

  const selectedBillTimeline = useMemo(() => {
    if (!selectedBill) {
      return [];
    }

    const rawBill = bills.find((bill) => bill.id === selectedBill.id);
    if (!rawBill) {
      return [];
    }

    return buildBillTimeline({
      bill: rawBill,
      bills,
      vendorsById,
      branchesById,
      payments,
      periodKey
    });
  }, [bills, branchesById, periodKey, payments, selectedBill, vendorsById]);

  const selectedBillSummary = useMemo(() => {
    if (selectedBillTimeline.length === 0) {
      return null;
    }

    const currentMonth = selectedBillTimeline[selectedBillTimeline.length - 1];
    const previousOutstanding = selectedBillTimeline
      .slice(0, -1)
      .reduce((sum, item) => sum + normalizeAmount(item.remainingAmount), 0);
    const totalOutstanding = selectedBillTimeline
      .reduce((sum, item) => sum + normalizeAmount(item.remainingAmount), 0);

    return {
      currentMonth,
      previousOutstanding,
      totalOutstanding,
      currentMonthOutstanding: normalizeAmount(currentMonth.remainingAmount),
      expectedAmount: normalizeAmount(currentMonth.expectedAmount),
      unpaidMonths: selectedBillTimeline.filter((item) => normalizeAmount(item.remainingAmount) > 0).length
    };
  }, [selectedBillTimeline]);

  const selectedVendorBills = useMemo(() => {
    if (!selectedVendorId) {
      return [];
    }

    return bills.filter((bill) => bill.vendor === selectedVendorId);
  }, [bills, selectedVendorId]);

  const paymentAllocationPreview = useMemo(() => {
    if (!selectedBillSummary) {
      return [];
    }

    return buildAllocationPreview(selectedBillTimeline, paymentForm.amount);
  }, [paymentForm.amount, selectedBillSummary, selectedBillTimeline]);

  const billPaymentLedgerRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return [...payments]
      .sort((first, second) => getPaymentTimestamp(second) - getPaymentTimestamp(first))
      .filter((payment) => {
        const paymentStatus = normalizePaymentStatus(payment.paymentStatus || payment.status);
        const matchesStatus = statusFilter === 'All' || paymentStatus === statusFilter;
        if (!matchesStatus) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        const searchBlob = [
          payment.billTitle,
          payment.vendorName,
          payment.billingPeriodLabel,
          payment.paymentMethod,
          payment.reference,
          payment.notes,
          payment.lotusTransactionStatus
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchBlob.includes(normalizedSearch);
      });
  }, [payments, searchTerm, statusFilter]);

  const summary = useMemo(() => billPaymentLedgerRows.reduce((accumulator, payment) => {
    accumulator.total += normalizeAmount(payment.amount);
    if (normalizePaymentStatus(payment.paymentStatus || payment.status).toLowerCase() === 'pending') {
      accumulator.pending += 1;
    }
    if (isSuccessfulBillPayment(payment)) {
      accumulator.paid += 1;
    }
    return accumulator;
  }, {
    total: 0,
    pending: 0,
    paid: 0
  }), [billPaymentLedgerRows]);

  const currentMonthPaymentsTotal = useMemo(() => {
    return payments.reduce((sum, payment) => (
      resolveBillPaymentPeriodKey(payment) === periodKey && isSuccessfulBillPayment(payment)
        ? sum + normalizeAmount(payment.amount)
        : sum
    ), 0);
  }, [payments, periodKey]);

  const availableVendors = useMemo(() => {
    const normalizedSearch = vendorPickerTerm.trim().toLowerCase();

    return vendors
      .filter((vendor) => bills.some((bill) => bill.vendor === vendor.id))
      .filter((vendor) => {
        if (!normalizedSearch) {
          return true;
        }

        const searchBlob = [
          vendor.vendorName,
          vendor.vendorContactPerson,
          vendor.vendorServices,
          vendor.vendorPhone,
          vendor.vendorEmail,
          vendor.vendorPaymentMethod,
          normalizeVendorPaymentMethod(vendor.vendorStatus || vendor.status)
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchBlob.includes(normalizedSearch);
      });
  }, [bills, vendorPickerTerm, vendors]);

  const openPaymentModal = () => {
    const initialVendor = availableVendors[0] || vendors[0] || null;
    const initialVendorId = initialVendor?.id || '';
    const initialVendorBills = bills.filter((bill) => bill.vendor === initialVendorId);
    const initialBill = initialVendorBills[0] || null;

    setSelectedVendorId(initialVendorId);
    setSelectedBillId(initialBill?.id || '');
    setVendorPickerTerm('');
    setPaymentForm(buildEmptyPaymentForm());
    setModalShow(true);
  };

  const closePaymentModal = () => {
    if (submittingPayment) return;
    setModalShow(false);
  };

  const handleSelectedVendorChange = (vendorId) => {
    setSelectedVendorId(vendorId);
    const nextVendorBills = bills.filter((bill) => bill.vendor === vendorId);
    setSelectedBillId(nextVendorBills.find((bill) => bill.remainingAmount > 0)?.id || nextVendorBills[0]?.id || '');
    setPaymentForm((currentForm) => ({
      ...currentForm,
      amount: ''
    }));
  };

  const handleSelectedBillChange = (billId) => {
    setSelectedBillId(billId);
    setPaymentForm((currentForm) => ({
      ...currentForm,
      amount: ''
    }));
  };

  const handlePaymentFormChange = (event) => {
    const { name, value } = event.target;
    setPaymentForm((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  };

  const applySuggestedAmount = (amount) => {
    setPaymentForm((currentForm) => ({
      ...currentForm,
      amount: String(normalizeAmount(amount))
    }));
  };

  const saveBillPayment = async () => {
    if (!selectedVendor || !selectedBill || !selectedBillSummary) {
      swal('Choose vendor', 'Select a vendor and bill before making a payment.', 'warning');
      return;
    }

    const amountValue = Number(paymentForm.amount);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      swal('Invalid amount', 'Payment amount should be greater than zero.', 'warning');
      return;
    }

    if (selectedBillSummary.totalOutstanding <= 0) {
      swal('No balance', 'This bill has no outstanding balance for the selected scope.', 'info');
      return;
    }

    if (amountValue > selectedBillSummary.totalOutstanding) {
      swal(
        'Amount too high',
        `You can only pay up to ${formatKes(selectedBillSummary.totalOutstanding)} for this bill.`,
        'warning'
      );
      return;
    }

    const paymentDateValue = new Date(paymentForm.paymentDate);
    if (Number.isNaN(paymentDateValue.getTime())) {
      swal('Invalid date', 'Please choose a valid payment date.', 'warning');
      return;
    }

    if (paymentAllocationPreview.length === 0) {
      swal('No allocation', 'There is no unpaid month to allocate this payment to.', 'warning');
      return;
    }

    const vendorMethod = normalizeVendorPaymentMethod(selectedVendor.vendorPaymentMethod || selectedVendor.paymentMethod);
    const lotusPaymentMethod = VENDOR_PAYMENT_METHOD_TO_LOTUS[vendorMethod];
    if (!lotusPaymentMethod) {
      swal(
        'Unsupported method',
        'Lotus only processes bank transfers, MPESA, paybill, and till payments for this flow.',
        'warning'
      );
      return;
    }

    const destinationDetails = getVendorDestinationDetails(selectedVendor, vendorMethod);
    if (!destinationDetails) {
      swal('Missing payout details', 'We could not prepare the payout destination for this vendor.', 'warning');
      return;
    }

    if (lotusPaymentMethod === 'pesalink' && (!destinationDetails.bankId || !destinationDetails.bankAccountNumber)) {
      swal('Missing bank details', 'Add the vendor bank name and account number first.', 'warning');
      return;
    }

    if (lotusPaymentMethod === 'mpesa' && !destinationDetails.phoneNumber) {
      swal('Missing phone number', 'Add the vendor phone number before submitting an MPESA payment.', 'warning');
      return;
    }

    if (lotusPaymentMethod === 'paybill' && (!destinationDetails.paybillNumber || !destinationDetails.paybillAccountNumber)) {
      swal('Missing paybill details', 'Add the vendor paybill number and account number first.', 'warning');
      return;
    }

    if (lotusPaymentMethod === 'till' && !destinationDetails.tillNumber) {
      swal('Missing till number', 'Add the vendor till number before submitting a till payment.', 'warning');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      swal('Session expired', 'Please sign in again before submitting a bill payment.', 'warning');
      return;
    }

    setSubmittingPayment(true);
    try {
      const idToken = await currentUser.getIdToken();
      const paymentTag = `bill-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const paymentTimestamp = Timestamp.fromDate(paymentDateValue);
      const createdAt = Timestamp.now();
      const allocationBreakdown = paymentAllocationPreview.map((allocation, index) => ({
        billId: selectedBill.id,
        vendorId: selectedVendor.id,
        periodKey: allocation.periodKey,
        periodLabel: allocation.periodLabel,
        expectedAmount: allocation.expectedAmount,
        allocatedAmount: allocation.allocatedAmount,
        remainingAmount: allocation.remainingAmount,
        allocationOrder: index + 1
      }));

      const lotusPayload = {
        amount: amountValue,
        payment_method: lotusPaymentMethod,
        recipient_name: destinationDetails.recipientName || selectedVendor.vendorName || 'Vendor',
        payment_purpose: `Bill payment for ${selectedBill.billTitle || 'fixed bill'} - ${selectedVendor.vendorName || 'vendor'} - ${getMonthLabel(periodKey)}`,
        payment_tag: paymentTag,
        payment_category: 'fixed_bill'
      };

      if (lotusPaymentMethod === 'pesalink') {
        lotusPayload.bank_id = destinationDetails.bankId;
        lotusPayload.bank_account_number = destinationDetails.bankAccountNumber;
      }

      if (lotusPaymentMethod === 'mpesa') {
        lotusPayload.phone_number = destinationDetails.phoneNumber;
      }

      if (lotusPaymentMethod === 'paybill') {
        lotusPayload.paybill_number = destinationDetails.paybillNumber;
        lotusPayload.paybill_account_number = destinationDetails.paybillAccountNumber;
      }

      if (lotusPaymentMethod === 'till') {
        lotusPayload.till_number = destinationDetails.tillNumber;
      }

      const lotusResponse = await fetch(`${LOTUS_API_BASE_URL}/payments`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(lotusPayload)
      });

      const lotusResponseBody = await lotusResponse.json().catch(() => null);
      if (!lotusResponse.ok) {
        const lotusErrorMessage = lotusResponseBody?.error?.message
          || lotusResponseBody?.error?.detail
          || lotusResponseBody?.error
          || lotusResponseBody?.message
          || 'Lotus rejected the payment request.';
        throw new Error(typeof lotusErrorMessage === 'string' ? lotusErrorMessage : 'Lotus rejected the payment request.');
      }

      const lotusPaymentDetails = extractLotusPaymentDetails(lotusResponseBody);
      const lotusPaymentData = lotusResponseBody?.data?.transaction
        || lotusResponseBody?.data
        || lotusResponseBody
        || {};
      const lotusTransactionId = lotusPaymentDetails.lotusTransactionId;
      const lotusTransactionStatus = lotusPaymentDetails.lotusTransactionStatus || 'pending';
      const lotusTransactionMessage = lotusPaymentDetails.lotusTransactionMessage
        || lotusResponseBody?.message
        || 'Payment submitted to Lotus.';

      const paymentRef = doc(collection(db, 'BillPayments'));
      const paymentPayload = {
        billId: selectedBill.id,
        billTitle: selectedBill.billTitle || 'Fixed bill',
        billType: 'Fixed Bill',
        vendorId: selectedVendor.id,
        vendorName: selectedVendor.vendorName || '',
        branchId: selectedVendor.branch || '',
        branchName: selectedVendor.branch === 'General'
          ? 'General'
          : branchesById[selectedVendor.branch] || 'Campus not set',
        payee: destinationDetails.recipientName || selectedVendor.vendorName || selectedBill.billTitle || 'Vendor',
        amount: amountValue,
        paymentDate: paymentTimestamp,
        paymentMethod: vendorMethod,
        paymentStatus: 'Pending',
        status: 'Pending',
        reference: '',
        notes: '',
        billingPeriodKey: periodKey,
        billingPeriodLabel: getMonthLabel(periodKey),
        expectedAmount: selectedBillSummary.expectedAmount,
        createdAt,
        createdBy: currentUser.uid,
        docId: paymentRef.id,
        transactionGroupId: paymentTag,
        lotusPaymentTag: paymentTag,
        lotusPaymentMethod,
        lotusTransactionId,
        lotusTransactionStatus,
        lotusTransactionMessage,
        lotusPaymentResponse: lotusPaymentData,
        allocationBreakdown,
        totalOutstandingAtPayment: selectedBillSummary.totalOutstanding,
        previousOutstandingAtPayment: selectedBillSummary.previousOutstanding,
        currentMonthOutstandingAtPayment: selectedBillSummary.currentMonthOutstanding,
        paymentDestinationSummary: lotusPaymentMethod === 'pesalink'
          ? {
              method: vendorMethod,
              bankId: destinationDetails.bankId,
              bankName: destinationDetails.bankName,
              bankCode: destinationDetails.bankCode,
              bankAccountLast4: destinationDetails.bankAccountNumber.slice(-4)
            }
          : lotusPaymentMethod === 'mpesa'
            ? {
                method: vendorMethod,
                phoneLast4: destinationDetails.phoneNumber.slice(-4)
              }
            : lotusPaymentMethod === 'paybill'
              ? {
                  method: vendorMethod,
                  paybillNumber: destinationDetails.paybillNumber,
                  accountLast4: destinationDetails.paybillAccountNumber.slice(-4)
                }
              : {
                  method: vendorMethod,
                  tillNumber: destinationDetails.tillNumber
                },
        sourcePage: 'pay-bills'
      };

      await setDoc(paymentRef, paymentPayload);
      const createdPayment = { id: paymentRef.id, ...paymentPayload };

      await logAdminActivity({
        action: 'Recorded Bill Payment',
        details: `Recorded ${formatKes(amountValue)} for ${selectedVendor.vendorName || 'vendor'} from pay bills page`,
        meta: {
          vendorId: selectedVendor.id,
          vendorName: selectedVendor.vendorName || '',
          billId: selectedBill.id,
          billTitle: selectedBill.billTitle || '',
          paymentTransactionGroupId: paymentTag,
          lotusTransactionId
        }
      });

      setPayments((currentPayments) => [createdPayment, ...currentPayments]);
      setModalShow(false);
      swal(
        'Payment submitted',
        `Submitted ${formatKes(amountValue)} to Lotus and saved the bill payment as pending while the transfer processes.`,
        'success'
      );
    } catch (error) {
      console.error('Failed to save bill payment:', error);
      swal('Error', error.message || 'The bill payment could not be submitted to Lotus.', 'error');
    } finally {
      setSubmittingPayment(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const reconcilePendingPayments = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser || billPaymentLedgerRows.length === 0) {
        return;
      }

      const pendingPayments = billPaymentLedgerRows.filter((payment) => {
        const status = normalizePaymentStatus(payment.paymentStatus || payment.status).toLowerCase();
        return Boolean(payment.lotusTransactionId) && ['pending', 'processing', 'submitted'].includes(status);
      });

      if (pendingPayments.length === 0) {
        return;
      }

      try {
        const idToken = await currentUser.getIdToken();
        const reconciledAt = Timestamp.now();

        await Promise.all(pendingPayments.map(async (payment) => {
          const response = await fetch(`${LOTUS_API_BASE_URL}/payments/${payment.lotusTransactionId}`, {
            headers: {
              Authorization: `Bearer ${idToken}`
            }
          });

          const payload = await response.json().catch(() => null);
          if (!response.ok) {
            return;
          }

          const lotusPaymentData = payload?.data?.transaction || payload?.data || payload || {};
          const lotusPaymentDetails = extractLotusPaymentDetails(payload);
          const lotusStatusRaw = lotusPaymentDetails.lotusTransactionStatus;
          const normalizedStatus = String(lotusStatusRaw || '').trim().toLowerCase();
          const paymentStatus = ['paid', 'processed', 'completed', 'successful', 'success', 'succeeded', 'approved'].includes(normalizedStatus)
            ? 'Paid'
            : ['failed', 'failure', 'rejected', 'declined', 'cancelled', 'canceled', 'error'].includes(normalizedStatus)
              ? 'Failed'
              : null;

          if (!paymentStatus || normalizePaymentStatus(payment.paymentStatus || payment.status).toLowerCase() === paymentStatus.toLowerCase()) {
            return;
          }

          const paymentRef = doc(db, 'BillPayments', payment.id);
          await setDoc(paymentRef, {
            paymentStatus,
            status: paymentStatus,
            lotusTransactionStatus: lotusStatusRaw || paymentStatus,
            lotusPaymentResponse: lotusPaymentData,
            lotusReconciledAt: reconciledAt,
            lotusReconciledSource: 'polling'
          }, { merge: true });

          if (!cancelled) {
            setPayments((currentPayments) => currentPayments.map((item) => (
              item.id === payment.id
                ? {
                    ...item,
                    paymentStatus,
                    status: paymentStatus,
                    lotusTransactionStatus: lotusStatusRaw || paymentStatus,
                    lotusPaymentResponse: lotusPaymentData,
                    lotusReconciledAt: reconciledAt,
                    lotusReconciledSource: 'polling'
                  }
                : item
            )));
          }
        }));
      } catch (error) {
        console.error('Failed to reconcile Lotus bill payments:', error);
      }
    };

    reconcilePendingPayments();
    const intervalId = window.setInterval(reconcilePendingPayments, LOTUS_RECONCILE_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [auth, billPaymentLedgerRows]);

  return (
    <div className="clientsComp applicationsPage payBillsPage">
      <div className="appsHeader">
        <div>
          <p className="appsKicker">Finance workflow</p>
          <h4>Pay bills</h4>
          <p className="appsSubtext">
            Select a vendor, choose the bill, review the running balance, then submit the payment to Lotus.
          </p>
        </div>
        <div className="appsHeaderStats">
          <div className="appsStatChip">
            <span>Records</span>
            <strong>{billPaymentLedgerRows.length}</strong>
          </div>
          <div className="appsStatChip">
            <span>Paid</span>
            <strong>{summary.paid}</strong>
          </div>
          <div className="appsStatChip">
            <span>Pending</span>
            <strong>{summary.pending}</strong>
          </div>
          <div className="appsStatChip">
            <span>This month</span>
            <strong>{formatKes(currentMonthPaymentsTotal)}</strong>
          </div>
        </div>
      </div>

      <div className="appsFiltersCard payBillsFiltersCard">
        <div className="appsFilterGroup payBillsFilterGroup">
          <Form.Control
            type="month"
            value={periodKey}
            onChange={(event) => setPeriodKey(event.target.value)}
            className="filterSelect"
          />
          <Form.Control
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search bill, vendor, method, or reference..."
            className="filterSelect"
          />
          <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="filterSelect">
            <option value="All">All statuses</option>
            <option value="Pending">Pending</option>
            <option value="Paid">Paid</option>
            <option value="Failed">Failed</option>
          </Form.Select>
        </div>
        <button type="button" className="opsPrimaryBtn fixedBillsPrimaryBtn" onClick={openPaymentModal}>
          <FontAwesomeIcon icon={faMoneyCheckDollar} /> Initiate payment
        </button>
      </div>

      <div className="appsTableCard">
        <div className="appsTableHeader">
          <h6>Bill payment records</h6>
          <span>{billPaymentLedgerRows.length} results</span>
        </div>
        <div className="appsTableWrap">
          {loading ? (
            <div className="opsLoadingCard">
              <Spinner animation="border" role="status" />
              <span>Loading bill payments...</span>
            </div>
          ) : billPaymentLedgerRows.length === 0 ? (
            <div className="appsEmptyState">
              <h6>No bill payments recorded</h6>
              <p>The payment register will fill up here once the first Lotus payment is submitted.</p>
            </div>
          ) : (
            <Table hover responsive className="appsTable">
              <thead>
                <tr>
                  <th>Bill</th>
                  <th>Vendor</th>
                  <th>Applied month</th>
                  <th>Paid on</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {billPaymentLedgerRows.map((payment) => (
                  <tr key={payment.id}>
                    <td>
                      <button
                        type="button"
                        className="payBillsRowLink"
                        onClick={() => payment.billId && navigate(`/view-fixed-bill/${payment.billId}`)}
                      >
                        {payment.billTitle || 'Fixed bill'}
                      </button>
                    </td>
                    <td>
                      <div className="opsDetailStack">
                        <strong>{payment.vendorName || 'Vendor'}</strong>
                        <small>{payment.branchName || 'Campus not set'}</small>
                      </div>
                    </td>
                    <td>{payment.billingPeriodLabel || getMonthLabel(resolveBillPaymentPeriodKey(payment))}</td>
                    <td>{formatDateTime(payment.paymentDate)}</td>
                    <td>{formatKes(payment.amount)}</td>
                    <td>{payment.paymentMethod || '—'}</td>
                    <td>
                      <span className={`opsStatusPill opsStatusPill--${paymentStatusTone(payment.paymentStatus || payment.status)}`}>
                        {normalizePaymentStatus(payment.paymentStatus || payment.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      <Modal show={modalShow} onHide={closePaymentModal} centered size="lg" contentClassName="paySalariesModalContent">
        <Modal.Header closeButton={!submittingPayment}>
          <Modal.Title>Initiate bill payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="paySalariesModalIntro">
            <p>
              Select a vendor, choose the bill, review the outstanding balance for the selected month and previous months,
              then submit one payment to Lotus.
            </p>
          </div>

          <div className="paySalariesModalControls">
            <Form.Group>
              <Form.Label>Search vendor</Form.Label>
              <Form.Control
                type="search"
                value={vendorPickerTerm}
                onChange={(event) => setVendorPickerTerm(event.target.value)}
                placeholder="Search vendor, service line, method..."
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Select vendor</Form.Label>
              <Form.Select
                value={selectedVendorId}
                onChange={(event) => handleSelectedVendorChange(event.target.value)}
              >
                <option value="">Choose vendor</option>
                {availableVendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.vendorName || 'Vendor'} • {normalizeVendorPaymentMethod(vendor.vendorPaymentMethod || vendor.paymentMethod) || 'Not set'}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Select bill</Form.Label>
              <Form.Select
                value={selectedBillId}
                onChange={(event) => handleSelectedBillChange(event.target.value)}
                disabled={!selectedVendorId}
              >
                <option value="">{selectedVendorId ? 'Choose bill' : 'Select vendor first'}</option>
                {selectedVendorBills.map((bill) => (
                  <option key={bill.id} value={bill.id}>
                    {bill.billTitle || 'Fixed bill'} • {formatKes(bill.remainingAmount)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>

          {!selectedVendor || !selectedBill ? (
            <div className="paySalariesModalState">
              <h6>Select a vendor and bill to continue</h6>
              <p>Balance details and payment allocation preview will appear here once you choose them.</p>
            </div>
          ) : (
            <>
              <div className="paySalariesModalSummary">
                <div className="paySalariesModalMetric">
                  <span>{getMonthLabel(periodKey)} balance</span>
                  <strong>{formatKes(selectedBillSummary?.currentMonthOutstanding || 0)}</strong>
                </div>
                <div className="paySalariesModalMetric">
                  <span>Previous months total</span>
                  <strong>{formatKes(selectedBillSummary?.previousOutstanding || 0)}</strong>
                </div>
                <div className="paySalariesModalMetric">
                  <span>Total payable now</span>
                  <strong>{formatKes(selectedBillSummary?.totalOutstanding || 0)}</strong>
                </div>
                <div className="paySalariesModalMetric">
                  <span>Unpaid months</span>
                  <strong>{selectedBillSummary?.unpaidMonths || 0}</strong>
                </div>
              </div>

              <div className="paySalariesModalEmployeeCard">
                <strong>{selectedVendor.vendorName || 'Vendor'}</strong>
                <span>{selectedBill.billTitle || 'Fixed bill'} • {branchesById[selectedVendor.branch] || selectedVendor.branch || 'Campus not set'}</span>
                <small>
                  Payment method {normalizeVendorPaymentMethod(selectedVendor.vendorPaymentMethod || selectedVendor.paymentMethod) || 'Not set'}
                </small>
              </div>

              <div className="paySalariesModalState paySalariesModalState--compact">
                <h6>Lotus payout destination</h6>
                <p>
                  {getVendorPayoutSummary(selectedVendor).method || 'Not set'}
                  {' '}• {getVendorPayoutSummary(selectedVendor).detail || 'Payment details not set'}
                </p>
              </div>

              <div className="paySalariesModalForm">
                <Form.Group className="mb-3">
                  <Form.Label>Payment amount</Form.Label>
                  <Form.Control
                    type="number"
                    name="amount"
                    value={paymentForm.amount}
                    onChange={handlePaymentFormChange}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    max={selectedBillSummary?.totalOutstanding || undefined}
                  />
                  <div className="paySalariesModalQuickActions">
                    <button
                      type="button"
                      className="btn btn-outline-dark btn-sm"
                      onClick={() => applySuggestedAmount(selectedBillSummary?.currentMonthOutstanding || 0)}
                    >
                      Use current month balance
                    </button>
                    <button
                      type="button"
                      className="btn btn-dark btn-sm"
                      onClick={() => applySuggestedAmount(selectedBillSummary?.totalOutstanding || 0)}
                    >
                      Clear full balance
                    </button>
                  </div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Payment date</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="paymentDate"
                    value={paymentForm.paymentDate}
                    onChange={handlePaymentFormChange}
                  />
                </Form.Group>
                <div className="paySalariesModalState paySalariesModalState--compact">
                  <h6>Payment method</h6>
                  <p>
                    {normalizeVendorPaymentMethod(selectedVendor.vendorPaymentMethod || selectedVendor.paymentMethod) || 'Not set'}
                    {normalizeVendorPaymentMethod(selectedVendor.vendorPaymentMethod || selectedVendor.paymentMethod) === 'Bank Transfer'
                      ? ' - bank details will be used for Lotus'
                      : normalizeVendorPaymentMethod(selectedVendor.vendorPaymentMethod || selectedVendor.paymentMethod) === 'MPESA'
                        ? ' - phone number will be used for Lotus'
                        : normalizeVendorPaymentMethod(selectedVendor.vendorPaymentMethod || selectedVendor.paymentMethod) === 'Paybill'
                          ? ' - paybill details will be used for Lotus'
                          : normalizeVendorPaymentMethod(selectedVendor.vendorPaymentMethod || selectedVendor.paymentMethod) === 'Till Number'
                            ? ' - till number will be used for Lotus'
                            : ' - this method cannot be sent to Lotus'}
                  </p>
                </div>
              </div>

              <div className="paySalariesModalAllocation">
                <div className="paySalariesModalAllocationHeader">
                  <div>
                    <h6>Allocation preview</h6>
                    <p>This payment will be distributed oldest-first so arrears stay accurate.</p>
                  </div>
                  <span>{paymentAllocationPreview.length} month{paymentAllocationPreview.length === 1 ? '' : 's'}</span>
                </div>

                {paymentAllocationPreview.length === 0 ? (
                  <div className="paySalariesModalState paySalariesModalState--compact">
                    <p>Enter a valid amount to preview how the payment will be allocated.</p>
                  </div>
                ) : (
                  <div className="paySalariesModalAllocationList">
                    {paymentAllocationPreview.map((allocation) => (
                      <div key={allocation.periodKey} className="paySalariesModalAllocationRow">
                        <div>
                          <strong>{allocation.periodLabel}</strong>
                          <span>Outstanding before payment {formatKes(allocation.remainingAmount)}</span>
                        </div>
                        <div className="paySalariesRecentAside">
                          <strong>{formatKes(allocation.allocatedAmount)}</strong>
                          <span>Applied now</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePaymentModal} disabled={submittingPayment}>
            Close
          </Button>
          <Button variant="dark" onClick={saveBillPayment} disabled={submittingPayment || !selectedBill}>
            {submittingPayment ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">Saving...</span>
              </>
            ) : (
              'Submit to Lotus'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default PayBillsComp;
